import { EmissionsReportCurrentT } from '@ruminati/types_v2/emissions-reports'
import { PropertyT } from '@ruminati/types_v2/property'
import {
  GrainAllocatedSummaryT,
  ReportResultsSummaryWithCropIntensityT,
} from '@ruminati/types_v2/report_results'
import { CropTypeT } from '@ruminati/veerg/modules/scope-1/ag-residue-management/2025-11-10-draft/schemas'

import { VaryingReportTypes } from '@/composables/analytics'
import { VaryingPropertyTypes } from '@/stores/properties'

type NewOrOldCropResult =
  | GrainAllocatedSummaryT
  | NonNullable<
      NonNullable<EmissionsReportCurrentT['results']>['commodities']['cropping']
    >['byCrop'][0]
  | NonNullable<ReportResultsSummaryWithCropIntensityT['intensitiesByCrop']>[0]

type BaseIntensityCommodity = {
  value: string
  label: string
  group: PropertyT['propertyType']
  reportResultsLabel?: string
  decimalPrecision: number
  unitLabel: string

  emissionsTypeLabel?: string // only really for sheep ATM

  intensityReference?: {
    nationalAverage: number
    suspiciousTolerance?: number
    author: string
    reportName: string
    reportLink: string
  }
  propertyTypeFilter: (_p: VaryingPropertyTypes) => boolean
  statisticalLookup?: {
    businessCount?: string
  }
}

export type StandardIntensityCommodity = BaseIntensityCommodity & {
  getCommodityEmissions: (r: VaryingReportTypes) => number | undefined
  getCommodityIntensity: (r: VaryingReportTypes) => number | undefined
}

export type CropIntensityCommodity = BaseIntensityCommodity & {
  cropIntensity: true
  isCrop: (cropResult: NewOrOldCropResult) => boolean
  getCommodityEmissions: (
    r: VaryingReportTypes,
    cropResult: NewOrOldCropResult,
  ) => number | undefined
  getCommodityIntensity: (
    r: VaryingReportTypes,
    cropResult: NewOrOldCropResult,
  ) => number | undefined
  findFirstCropInReport: (r: VaryingReportTypes) => NewOrOldCropResult | undefined
}

export type IntensityCommodity = StandardIntensityCommodity | CropIntensityCommodity

export type MajorCommodity = {
  value: string
  label: string
  getCommodityEmissions: (r: VaryingReportTypes) => number | undefined
}

function finiteOrUndefined(value: number | undefined): number | undefined {
  return value !== undefined && Number.isFinite(value) ? value : undefined
}

export const Beef: MajorCommodity = {
  value: 'beef',
  label: 'Beef',
  getCommodityEmissions: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return finiteOrUndefined(report.results.intensity.netEmissionsBeef ?? undefined)
    } else if ('commodities' in report.results) {
      return finiteOrUndefined(report.results.commodities.beefPasture?.totals.grossTco2e)
    }
    return
  },
}

export const Sheep: MajorCommodity = {
  value: 'sheep',
  label: 'Sheep',
  getCommodityEmissions: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return finiteOrUndefined(report.results.intensity.netEmissionsSheep ?? undefined)
    } else if ('commodities' in report.results) {
      return finiteOrUndefined(report.results.commodities.sheep?.totals.grossTco2e)
    }
    return
  },
}

export const Crops: MajorCommodity = {
  value: 'crops',
  label: 'Crops',
  getCommodityEmissions: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return report.results.intensity.netGrainsEmissions ?? undefined
    } else if ('commodities' in report.results) {
      return report.results.commodities.cropping?.totals.grossTco2e
    }
    return
  },
}

export const DairyMilk: MajorCommodity = {
  value: 'dairyMilk',
  label: 'Dairy - Milk',
  getCommodityEmissions: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return finiteOrUndefined(report.results.intensity.netDairyMilkEmissions ?? undefined)
    } else if ('commodities' in report.results) {
      return finiteOrUndefined(report.results.commodities.dairy?.byProduct?.milk.totals.grossTco2e)
    }
    return
  },
}

export const DairyMeat2: MajorCommodity = {
  value: 'dairyMeat',
  label: 'Dairy - Meat',
  getCommodityEmissions: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return finiteOrUndefined(report.results.intensity.netDairyMeatEmissions ?? undefined)
    } else if ('commodities' in report.results) {
      return finiteOrUndefined(report.results.commodities.dairy?.byProduct?.meat.totals.grossTco2e)
    }
    return
  },
}

export const Feedlot: MajorCommodity = {
  value: 'feedlot',
  label: 'Feedlot - All Scopes',
  getCommodityEmissions: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return finiteOrUndefined(report.results.intensity.netFeedlotEmissions ?? undefined)
    } else if ('commodities' in report.results) {
      return finiteOrUndefined(report.results.commodities.beefFeedlot?.totals.grossTco2e)
    }
    return
  },
}

export const Processor: MajorCommodity = {
  value: 'processor',
  label: 'Processor - All Scopes',
  getCommodityEmissions: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return report.results.intensity.netProcessorEmissions ?? undefined
    } else if ('commodities' in report.results) {
      return undefined
    }
    return
  },
}

export const BeefCommodity: StandardIntensityCommodity = {
  value: 'beef',
  label: 'Beef',
  group: 'farm',
  decimalPrecision: 1,
  unitLabel: 'kg CO2-e / kg LWT',
  getCommodityEmissions: (r) => Beef.getCommodityEmissions(r),
  getCommodityIntensity: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return finiteOrUndefined(report.results.intensity.kgCo2PerKgBeefMeatSold ?? undefined)
    } else if ('commodities' in report.results) {
      return finiteOrUndefined(
        report.results.commodities.beefPasture?.intensity.excludingSequestration
          ?.kgCo2ePerKgLiveweightSold,
      )
    }
    return
  },
  intensityReference: {
    nationalAverage: 12.6,
    author: 'MLA, 2019',
    reportName: 'Australian Beef Industry 35-year environmental impact trends analysis',
    reportLink:
      'https://www.goodmeat.com.au/globalassets/mla-corporate/research-and-development/final-reports/2021/b.cch.2109--project-overview.pdf',
  },
  propertyTypeFilter: (p) => p.cattleFarm === true,
  statisticalLookup: {
    businessCount: 'cattle.numBusinesses',
  },
}

export const SheepMeatCommodity: StandardIntensityCommodity = {
  value: 'sheepMeat',
  label: 'Sheep Meat',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg LWT',
  decimalPrecision: 1,
  getCommodityEmissions: (r) => Sheep.getCommodityEmissions(r),
  getCommodityIntensity: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return finiteOrUndefined(report.results.intensity.kgCo2PerKgSheepMeatSold ?? undefined)
    } else if ('commodities' in report.results) {
      return finiteOrUndefined(
        report.results.commodities.sheep?.intensity.excludingSequestration
          ?.kgCo2ePerKgLiveweightSold,
      )
    }
    return
  },
  intensityReference: {
    nationalAverage: 6.8,
    author: 'MLA, 2021',
    reportName: 'Sheep Sustainability Framework',
    reportLink:
      'https://www.sheepsustainabilityframework.com.au/globalassets/sheep-sustainability/media/ssf-final-21_lr.pdf',
  },
  propertyTypeFilter: (p) => p.sheepFarm === true,
  statisticalLookup: {
    businessCount: 'sheep.numBusinesses',
  },
}

export const WoolCommodity: StandardIntensityCommodity = {
  value: 'wool',
  label: 'Wool',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg greasy wool',
  decimalPrecision: 1,
  getCommodityEmissions: (r) => Sheep.getCommodityEmissions(r),
  getCommodityIntensity: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return finiteOrUndefined(report.results.intensity.kgCo2PerKgGreasyWool ?? undefined)
    } else if ('commodities' in report.results) {
      return finiteOrUndefined(
        report.results.commodities.sheep?.intensity.excludingSequestration?.kgCo2ePerKgWool,
      )
    }
    return
  },
  intensityReference: {
    nationalAverage: 20.5,
    author: 'MLA, 2021',
    reportName: 'Sheep Sustainability Framework',
    reportLink:
      'https://www.sheepsustainabilityframework.com.au/globalassets/sheep-sustainability/media/ssf-final-21_lr.pdf',
  },
  propertyTypeFilter: (p) => p.sheepFarm === true,
  statisticalLookup: {
    businessCount: 'sheep.numBusinesses',
  },
}

// export const CropCommodity: IntensityCommodity = {
//   value: 'crop',
//   label: 'Crops (overall)',
//   group: 'farm',
//   unitLabel: 'kg CO2-e / kg Crop',
//   decimalPrecision: 2,
//   emissionsFieldLookup: 'emissions.grains',
//   intensityFieldLookup: 'intensity.kgCo2PerKgGrainProduced',
//   intensityReference: {
//     nationalAverage: 0.275,
//     suspiciousTolerance: 10,
//     author: 'CSIRO, 2011',
//     reportName:
//       'Carbon footprint for Australian agricultural products and downstream food products in the supermarket',
//     reportLink: 'https://publications.csiro.au/rpr/download?pid=csiro:EP103907&dsid=DS',
//   },
//   propertyTypeFilter: (p) => p.grainFarm === true,
// }

function isCropType(cropResult: NewOrOldCropResult, oldId: number, newType: CropTypeT): boolean {
  if ('totalEmissions' in cropResult) {
    return cropResult.cropTypeId === oldId
  } else if ('metadata' in cropResult) {
    return cropResult.metadata.cropType === newType
  }
  return false
}

function getCropEmissions(
  report: VaryingReportTypes,
  cropResult: NewOrOldCropResult,
): number | undefined {
  if (!report.results) return
  if ('totalEmissions' in cropResult) {
    return cropResult.totalEmissions ?? undefined
  } else if ('metadata' in cropResult) {
    return cropResult.totals.grossTco2e
  }
  return
}

function findFirstCropInReport(
  report: VaryingReportTypes,
  oldId: number,
  newType: CropTypeT,
): NewOrOldCropResult | undefined {
  if (!report.results) return
  if (report.isGafReport) {
    if ('intensitiesByCrop' in report.results) {
      return report.results.intensitiesByCrop?.find((c) => isCropType(c, oldId, newType))
    }
  } else if ('commodities' in report.results) {
    return report.results.commodities.cropping?.byCrop.find((c) => isCropType(c, oldId, newType))
  }
  return
}

function getCropIntensity(
  report: VaryingReportTypes,
  cropResult: NewOrOldCropResult,
): number | undefined {
  if (!report.results) return
  if ('totalEmissions' in cropResult) {
    return cropResult.intensity ?? undefined
  } else if ('metadata' in cropResult) {
    return cropResult.intensity.excludingSequestration.kgCo2ePerKgCropProduced
  }
  return
}

export const WheatCommodity: CropIntensityCommodity = {
  value: 'wheat',
  label: 'Wheat',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Wheat',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 1, 'wheat'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  findFirstCropInReport: (report) => findFirstCropInReport(report, 1, 'wheat'),
  intensityReference: {
    nationalAverage: 0.27,
    suspiciousTolerance: 7,
    author: 'CSIRO, 2011',
    reportName:
      'Carbon footprint for Australian agricultural products and downstream food products in the supermarket',
    reportLink: 'https://publications.csiro.au/rpr/download?pid=csiro:EP103907&dsid=DS',
  },
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const BarleyCommodity: IntensityCommodity = {
  value: 'barley',
  label: 'Barley',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Barley',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 2, 'barley'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  findFirstCropInReport: (report) => findFirstCropInReport(report, 2, 'barley'),
  intensityReference: {
    nationalAverage: 0.11,
    suspiciousTolerance: 7,
    author: 'CSIRO, 2011',
    reportName:
      'Carbon footprint for Australian agricultural products and downstream food products in the supermarket',
    reportLink: 'https://publications.csiro.au/rpr/download?pid=csiro:EP103907&dsid=DS',
  },
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const MaizeCommodity: IntensityCommodity = {
  value: 'maize',
  label: 'Maize',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Maize',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 3, 'maize'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  findFirstCropInReport: (report) => findFirstCropInReport(report, 3, 'maize'),
  intensityReference: {
    nationalAverage: 0.14,
    suspiciousTolerance: 7,
    author: 'ALCAS, 2023',
    reportName: 'Australian Life Cycle Inventory Database',
    reportLink: 'https://www.auslci.com.au/index.php/EmissionFactors',
  },
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const OatsCommodity: IntensityCommodity = {
  value: 'oats',
  label: 'Oats',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Oats',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 4, 'oats'),
  findFirstCropInReport: (report) => findFirstCropInReport(report, 4, 'oats'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  intensityReference: {
    nationalAverage: 0.31,
    suspiciousTolerance: 7,
    author: 'ALCAS, 2023',
    reportName: 'Australian Life Cycle Inventory Database',
    reportLink: 'https://www.auslci.com.au/index.php/EmissionFactors',
  },
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const RiceCommodity: IntensityCommodity = {
  value: 'rice',
  label: 'Rice',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Rice',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 5, 'rice'),
  findFirstCropInReport: (report) => findFirstCropInReport(report, 5, 'rice'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  intensityReference: {
    nationalAverage: 0.48,
    suspiciousTolerance: 7,
    author: 'ABARES, 2019',
    reportName: 'International farm emissions intensity statistics',
    reportLink:
      'https://www.agriculture.gov.au/abares/research-topics/trade/climate/international-emissions-intensity-statistics',
  },
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const SorghumCommodity: IntensityCommodity = {
  value: 'sorghum',
  label: 'Sorghum',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Sorghum',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 6, 'sorghum'),
  findFirstCropInReport: (report) => findFirstCropInReport(report, 6, 'sorghum'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  intensityReference: {
    nationalAverage: 0.36,
    suspiciousTolerance: 7,
    author: 'ALCAS, 2023',
    reportName: 'Australian Life Cycle Inventory Database',
    reportLink: 'https://www.auslci.com.au/index.php/EmissionFactors',
  },
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const TriticaleCommodity: IntensityCommodity = {
  value: 'triticale',
  label: 'Triticale',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Triticale',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 7, 'triticale'),
  findFirstCropInReport: (report) => findFirstCropInReport(report, 7, 'triticale'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  intensityReference: {
    nationalAverage: 0.34,
    suspiciousTolerance: 7,
    author: 'ALCAS, 2023',
    reportName: 'Australian Life Cycle Inventory Database',
    reportLink: 'https://www.auslci.com.au/index.php/EmissionFactors',
  },
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const OtherCerealsCommodity: IntensityCommodity = {
  value: 'otherCereals',
  label: 'Other Cereals',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Other Cereals',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 8, 'otherCereals'),
  findFirstCropInReport: (report) => findFirstCropInReport(report, 8, 'otherCereals'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const PulsesCommodity: IntensityCommodity = {
  value: 'pulses',
  label: 'Pulses',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Pulses',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 9, 'pulses'),
  findFirstCropInReport: (report) => findFirstCropInReport(report, 9, 'pulses'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  intensityReference: {
    nationalAverage: 0.12,
    suspiciousTolerance: 7,
    author: 'ALCAS, 2023',
    reportName: 'Australian Life Cycle Inventory Database',
    reportLink: 'https://www.auslci.com.au/index.php/EmissionFactors',
  },
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const TubersRootsCommodity: IntensityCommodity = {
  value: 'tubersRoots',
  label: 'Tubers & Roots',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Tubers & Roots',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 10, 'tubersAndRoots'),
  findFirstCropInReport: (report) => findFirstCropInReport(report, 10, 'tubersAndRoots'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  intensityReference: {
    nationalAverage: 0.21,
    suspiciousTolerance: 7,
    author: 'ALCAS, 2023',
    reportName: 'Australian Life Cycle Inventory Database',
    reportLink: 'https://www.auslci.com.au/index.php/EmissionFactors',
  },
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const PeanutCommodity: IntensityCommodity = {
  value: 'peanut',
  label: 'Peanuts',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Peanuts',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 11, 'peanuts'),
  findFirstCropInReport: (report) => findFirstCropInReport(report, 11, 'peanuts'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  intensityReference: {
    nationalAverage: -0.1,
    suspiciousTolerance: 7,
    author: 'ALCAS, 2023',
    reportName: 'Australian Life Cycle Inventory Database',
    reportLink: 'https://www.auslci.com.au/index.php/EmissionFactors',
  },
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const SugarCaneCommodity: IntensityCommodity = {
  value: 'sugarcane',
  label: 'Sugarcane',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Sugarcane',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 12, 'sugarCane'),
  findFirstCropInReport: (report) => findFirstCropInReport(report, 12, 'sugarCane'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  intensityReference: {
    nationalAverage: 0.05,
    suspiciousTolerance: 7,
    author: 'ALCAS, 2023',
    reportName: 'Australian Life Cycle Inventory Database',
    reportLink: 'https://www.auslci.com.au/index.php/EmissionFactors',
  },
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const CottonCommodity: IntensityCommodity = {
  value: 'cotton',
  label: 'Cotton',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Cotton',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 13, 'cotton'),
  findFirstCropInReport: (report) => findFirstCropInReport(report, 13, 'cotton'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  intensityReference: {
    nationalAverage: 0.46,
    suspiciousTolerance: 7,
    author: 'ALCAS, 2023',
    reportName: 'Australian Life Cycle Inventory Database',
    reportLink: 'https://www.auslci.com.au/index.php/EmissionFactors',
  },
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const HopsCommodity: IntensityCommodity = {
  value: 'hops',
  label: 'Hops',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Hops',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 14, 'hops'),
  findFirstCropInReport: (report) => findFirstCropInReport(report, 14, 'hops'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const OilseedsCommodity: IntensityCommodity = {
  value: 'oilseeds',
  label: 'Oilseeds',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Oilseeds',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 15, 'oilseeds'),
  findFirstCropInReport: (report) => findFirstCropInReport(report, 15, 'oilseeds'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  intensityReference: {
    nationalAverage: 0.48,
    suspiciousTolerance: 7,
    author: 'ALCAS, 2023',
    reportName: 'Australian Life Cycle Inventory Database',
    reportLink: 'https://www.auslci.com.au/index.php/EmissionFactors',
  },
  propertyTypeFilter: (p) => p.grainFarm === true,
}

export const ForageCropsCommodity: IntensityCommodity = {
  value: 'forageCrops',
  label: 'Forage Crops',
  group: 'farm',
  unitLabel: 'kg CO2-e / kg Forage Crops',
  decimalPrecision: 2,
  cropIntensity: true,
  isCrop: (cropResult) => isCropType(cropResult, 16, 'forageCrops'),
  findFirstCropInReport: (report) => findFirstCropInReport(report, 16, 'forageCrops'),
  getCommodityEmissions: getCropEmissions,
  getCommodityIntensity: getCropIntensity,
  propertyTypeFilter: (p) => p.grainFarm === true,
}

// // export const FeedlotScope1and2: IntensityCommodity = {
// //   value: 'feedlotScopes1And2',
// //   label: 'Scope 1 & 2',
// //   group: 'feedlot',
// //   reportResultsLabel: 'Scope 1 & 2',
// //   decimalPrecision: 1,
// //   unitLabel: 'kg CO2-e / kg gain',
// //   emissionsFieldLookup: 'intensity.netFeedlotEmissions',
// //   intensityFieldLookup: 'intensity.kgCo2PerKgMeatFedScope1and2',
// //   intensityReference: {
// //     nationalAverage: 3.6,
// //     author: 'MLA, 2021',
// //     reportName: 'Moving towards carbon neutrality – Opportunities for the feedlot industry',
// //     reportLink: 'https://www.mla.com.au/globalassets/mla-corporate/research-and-development/program-areas/feeding-finishing-and-nutrition/pathways-to-carbon-neutrality-for-australian-feedlots.pdf'
// //   },
// //   propertyTypeFilter: (p) => p.propertyType === 'feedlot',
// // }

export const FeedlotAllScopes: StandardIntensityCommodity = {
  value: 'feedlotAllScopes',
  group: 'feedlot',
  reportResultsLabel: 'All Scopes',
  label: 'All Scopes',
  decimalPrecision: 1,
  unitLabel: 'kg CO2-e / kg sold',
  getCommodityEmissions: (r) => Feedlot.getCommodityEmissions(r),
  getCommodityIntensity: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return finiteOrUndefined(report.results.intensity.kgCo2PerKgMeatFed ?? undefined)
    } else if ('commodities' in report.results) {
      return undefined
    }
    return
  },
  intensityReference: {
    nationalAverage: 9.7,
    author: 'MLA, 2021',
    reportName: 'Moving towards carbon neutrality – Opportunities for the feedlot industry',
    reportLink:
      'https://www.mla.com.au/globalassets/mla-corporate/research-and-development/program-areas/feeding-finishing-and-nutrition/pathways-to-carbon-neutrality-for-australian-feedlots.pdf',
  },
  propertyTypeFilter: (p) => p.propertyType === 'feedlot',
}

// export const ProcessorScope1and2: StandardIntensityCommodity = {
//   value: 'processor',
//   reportResultsLabel: 'Scope 1 & 2',
//   group: 'processor',
//   label: 'Scope 1 & 2',
//   decimalPrecision: 1,
//   unitLabel: 'kg CO2-e / t HSCW',
//   getCommodityEmissions: (r) => Processor.getCommodityEmissions(r),
//   getCommodityIntensity: (report) => {
//     if (!report.results) return
//     if ('intensity' in report.results) {
//       return report.results.intensity.kgCo2PerKgMeatFed ?? undefined
//     } else if ('commodities' in report.results) {
//       return undefined
//     }
//     return
//   },
//   intensityReference: {
//     nationalAverage: 447,
//     author: 'AMPC, 2022',
//     reportName: 'Environmental Performance Review 2022',
//     reportLink:
//       'https://www.ampc.com.au/getmedia/994db052-d261-42fc-93aa-48d2be6a8d64/Final-Report-EPR-2022.pdf?ext=.pdf',
//   },
//   propertyTypeFilter: (p) =>
//     p.propertyType === 'processor' || p.propertyType === 'further-processor',
// }

export const ProcessorAllScopes: StandardIntensityCommodity = {
  value: 'processorAllScopes',
  reportResultsLabel: 'All Scopes',
  group: 'processor',
  label: 'All Scopes',
  decimalPrecision: 1,
  unitLabel: 'kg CO2-e / t HSCW',
  getCommodityEmissions: (r) => Processor.getCommodityEmissions(r),
  getCommodityIntensity: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return report.results.intensity.kgCO2ePerTonneHCSW ?? undefined
    } else if ('commodities' in report.results) {
      return undefined
    }
    return
  },
  intensityReference: {
    nationalAverage: 13.8,
    author: 'MLA, 2019',
    reportName: 'Beef Industry 35 year trends analysis.',
    reportLink:
      'https://www.mla.com.au/globalassets/mla-corporate/research-and-development/final-reports/2021/b.cch.2109--project-overview.pdf',
  },
  propertyTypeFilter: (p) =>
    p.propertyType === 'processor' || p.propertyType === 'further-processor',
}

export const DairyFPCM: StandardIntensityCommodity = {
  value: 'dairyFPCM',
  label: 'Milk',
  group: 'dairy',
  decimalPrecision: 2,
  unitLabel: 'kg CO2-e / kg FPCM',
  getCommodityEmissions: (r) => DairyMilk.getCommodityEmissions(r),
  getCommodityIntensity: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return finiteOrUndefined(report.results.intensity.kgCo2PerKgMilkFPCM ?? undefined)
    } else if ('commodities' in report.results) {
      return undefined
    }
    return
  },
  intensityReference: {
    nationalAverage: 0.91,
    author: 'Dairy Australia',
    reportName: 'Australian Dairy Carbon Calculator Manual, Feb 2025',
    reportLink:
      'https://cdn-prod.dairyaustralia.com.au/-/media/project/dairy-australia-sites/national-home/resources/climate-environment/australian-carbon-calculator-v5/adcc-manual-all-sections-v54.pdf?rev=5319f540a4a94d24a9d1d3116a244bf0',
  },
  propertyTypeFilter: (p) => p.propertyType === 'dairy',
}

export const DairyMilkSolids: StandardIntensityCommodity = {
  value: 'dairyMilkSolids',
  label: 'Milk Solids',
  group: 'dairy',
  decimalPrecision: 2,
  unitLabel: 'kg CO2-e / kg Milk Solids',
  getCommodityEmissions: (r) => DairyMilk.getCommodityEmissions(r),
  getCommodityIntensity: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return finiteOrUndefined(report.results.intensity.kgCo2PerKgMilk ?? undefined)
    } else if ('commodities' in report.results) {
      return undefined
    }
    return
  },
  intensityReference: {
    nationalAverage: 12.77,
    author: 'Dairy Australia',
    reportName: 'Australian Dairy Carbon Calculator Manual, Feb 2025',
    reportLink:
      'https://cdn-prod.dairyaustralia.com.au/-/media/project/dairy-australia-sites/national-home/resources/climate-environment/australian-carbon-calculator-v5/adcc-manual-all-sections-v54.pdf?rev=5319f540a4a94d24a9d1d3116a244bf0',
  },
  propertyTypeFilter: (p) => p.propertyType === 'dairy',
}

export const DairyMeat: StandardIntensityCommodity = {
  value: 'dairyMeat',
  label: 'Dairy Meat',
  group: 'dairy',
  decimalPrecision: 2,
  unitLabel: 'kg CO2-e / kg LWT',
  getCommodityEmissions: (r) => DairyMeat.getCommodityEmissions(r),
  getCommodityIntensity: (report) => {
    if (!report.results) return
    if ('intensity' in report.results) {
      return finiteOrUndefined(report.results.intensity.kgCo2PerKgDairyMeat ?? undefined)
    } else if ('commodities' in report.results) {
      return undefined
    }
    return
  },
  intensityReference: {
    nationalAverage: 4.67,
    author: 'Dairy Australia',
    reportName: 'Australian Dairy Carbon Calculator Manual, Feb 2025',
    reportLink:
      'https://cdn-prod.dairyaustralia.com.au/-/media/project/dairy-australia-sites/national-home/resources/climate-environment/australian-carbon-calculator-v5/adcc-manual-all-sections-v54.pdf?rev=5319f540a4a94d24a9d1d3116a244bf0',
  },
  propertyTypeFilter: (p) => p.propertyType === 'dairy',
}

export const CommodityConfig: IntensityCommodity[] = [
  BeefCommodity,
  SheepMeatCommodity,
  WoolCommodity,
  // CropCommodity,
  DairyMeat,
  DairyMilkSolids,
  DairyFPCM,
  // // FeedlotScope1and2,
  FeedlotAllScopes,
  // ProcessorScope1and2,
  ProcessorAllScopes,

  WheatCommodity,
  BarleyCommodity,
  ForageCropsCommodity,
  OilseedsCommodity,
  HopsCommodity,
  CottonCommodity,
  SugarCaneCommodity,
  PeanutCommodity,
  TubersRootsCommodity,
  PulsesCommodity,
  OtherCerealsCommodity,
  TriticaleCommodity,
  SorghumCommodity,
  RiceCommodity,
  OatsCommodity,
  MaizeCommodity,
]

export const MajorCommodityConfig: MajorCommodity[] = [
  Beef,
  Sheep,
  Crops,
  DairyMeat2,
  DairyMilk,
  Feedlot,
]
